/**
 * CasesEinstellungen — User-Preferences für das Cases-Modul.
 *
 * Aktuell nur Auto-Refresh-Intervall. Lokale Settings pro User (localStorage),
 * keine globale Konfiguration. Admin-Configuration läuft weiter über
 * /cases/konfiguration.
 *
 * @page /cases/einstellungen
 * @roles all authenticated
 * @i18n casesEinstellungen
 * @since #1247 v2.540.0
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useCasesRefreshInterval from '../hooks/useCasesRefreshInterval';

export default function CasesEinstellungen() {
  const { t } = useLanguage();
  const [refreshSeconds, setRefreshSeconds] = useCasesRefreshInterval();

  const options = [
    { value: 30,  label: t('casesEinstellungen.refresh.opt30s',  'Alle 30 Sekunden') },
    { value: 60,  label: t('casesEinstellungen.refresh.opt1m',   'Jede Minute (empfohlen)') },
    { value: 300, label: t('casesEinstellungen.refresh.opt5m',   'Alle 5 Minuten') },
    { value: 0,   label: t('casesEinstellungen.refresh.optOff',  'Manuell aktualisieren') },
  ];

  return (
    <div className="flex flex-col h-full bg-surface">
      <div className="border-b border-line px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/cases/liste"
            className="p-1.5 rounded-card hover:bg-surface-hover text-content-tertiary hover:text-content transition-colors"
            title={t('common.back', 'Zurück')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <Settings className="w-5 h-5 text-content-tertiary" />
          <div>
            <h1 className="text-base font-bold text-content">
              {t('casesEinstellungen.title', 'Cases — Einstellungen')}
            </h1>
            <p className="text-[11px] text-content-tertiary">
              {t('casesEinstellungen.subtitle', 'Persönliche Einstellungen für deine Cases-Ansicht')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 lg:px-8 py-6">
        <div className="max-w-2xl space-y-6">
          <section className="bg-surface-card border border-line rounded-card p-5">
            <h2 className="text-sm font-bold text-content mb-1">
              {t('casesEinstellungen.refresh.heading', 'Automatisches Aktualisieren')}
            </h2>
            <p className="text-xs text-content-secondary mb-4">
              {t('casesEinstellungen.refresh.helpText', 'Wie oft soll die Cases-Liste automatisch neu geladen werden?')}
            </p>

            <div className="space-y-2">
              {options.map(opt => {
                const isActive = refreshSeconds === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-card border cursor-pointer transition-colors ${
                      isActive
                        ? 'border-[#338edc] bg-[#338edc]/5'
                        : 'border-line bg-surface-card hover:border-line-hover'
                    }`}
                  >
                    <input
                      type="radio"
                      name="cases-refresh"
                      value={opt.value}
                      checked={isActive}
                      onChange={() => setRefreshSeconds(opt.value)}
                      className="w-4 h-4 text-[#338edc] focus:ring-[#338edc]/30"
                    />
                    <span className="text-sm text-content">{opt.label}</span>
                  </label>
                );
              })}
            </div>

            <p className="text-[11px] text-content-tertiary mt-3">
              {t('casesEinstellungen.refresh.note', 'Diese Einstellung gilt nur für dich und wird in deinem Browser gespeichert.')}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
